import i18n from 'config/i18n';
import dayjs, { Dayjs } from 'dayjs';
import {
  ActivityResponse,
  ClientResponse,
  EmployeeResponse,
  EmployeeTypeResponse,
  LocationType,
  //MatchCandidateResponse,
  ProjectResponse,
  SchedulingLabel,
  WorkPlaceResponse
} from 'generated';
import * as yup from 'yup';

import { getLocale } from 'utils/date';
import { stringToFloat } from 'utils/misc';

const appointmentSchema = () => {
  const t = i18n.getFixedT(getLocale(), 'common');

  return yup.object({
    organizationalUnit: yup.mixed().when(['schedulingLabel', 'hasProject'], {
      is: (schedulingLabel: string, hasProject?: boolean) => {
        return schedulingLabel !== 'PROJECT' && !hasProject;
      },
      then: yup
        .mixed()
        .required(t('validationFieldRequired', { name: t('planUnits') }))
    }),
    workPlace: yup
      .mixed<WorkPlaceResponse>()
      // Note: 'hasProject' condition is temporary work-around to mitigate the backend always returning 'WORKPLACE' as schedulingLabel
      .when(['schedulingLabel', 'hasProject'], {
        is: (schedulingLabel: string, hasProject?: boolean) => {
          return schedulingLabel === 'WORKPLACE' && !hasProject;
        },
        then: yup.mixed<WorkPlaceResponse>().required(
          t('validationFieldRequired', {
            name: t('workPlace')
          })
        )
      }),
    project: yup
      .mixed<ProjectResponse>()
      // Note: 'hasProject' condition is temporary work-around to mitigate the backend always returning 'WORKPLACE' as schedulingLabel
      .when(['schedulingLabel', 'hasProject'], {
        is: (schedulingLabel: string, hasProject?: boolean) => {
          return schedulingLabel === 'PROJECT' || hasProject;
        },
        then: yup
          .mixed<ProjectResponse>()
          .test(
            'employee-in-project',
            () => t('activityPlanning.employeeNoProjectParticipant'),
            function (project) {
              if (!project) {
                return true;
              }

              const employee = this.parent.employee;
              const startDate = this.parent.startDate as Dayjs;
              return (
                project.participants &&
                ((employee &&
                  project.participants.some(
                    (participant) =>
                      participant.participantEntityId == employee.entityId &&
                      participant.validFrom &&
                      dayjs(participant.validFrom).isBefore(startDate) &&
                      participant.validTo &&
                      dayjs(participant.validTo).isAfter(startDate)
                  )) ||
                  !employee)
              );
            }
          )
          .required(
            t('validationFieldRequired', {
              name: t('activityPlanning.project')
            })
          )
      }),
    client: yup.mixed<ClientResponse>().nullable(true).notRequired(),
    employee: yup
      .mixed<EmployeeResponse>()
      .nullable(true)
      .when(['schedulingLabel', 'hasProject'], {
        is: (schedulingLabel: string, hasProject?: boolean) => {
          return schedulingLabel === 'PROJECT' || hasProject;
        },
        then: yup
          .mixed<EmployeeResponse>()
          .nullable(true)
          .required(
            t('validationFieldRequired', {
              name: t('employee')
            })
          ),
        otherwise: yup.mixed<EmployeeResponse>().nullable(true).notRequired()
      }),
    // candidateEmployment: yup
    //   .mixed<MatchCandidateResponse>()
    //   .nullable(true)
    //   .when(['schedulingLabel'], {
    //     is: (schedulingLabel: string) => {
    //       return schedulingLabel === 'VV';
    //     },
    //     then: yup
    //       .mixed<MatchCandidateResponse>()
    //       .nullable(true)
    //       .required(
    //         t('validationFieldRequired', {
    //           name: t('employee')
    //         })
    //       ),
    //     otherwise: yup
    //       .mixed<MatchCandidateResponse>()
    //       .nullable(true)
    //       .notRequired()
    //   }),
    employeeType: yup
      .mixed<EmployeeTypeResponse>()
      .nullable(true)
      .notRequired(),
    activity: yup
      .mixed<ActivityResponse>()
      .nullable(true)
      .test(
        'activity-selected',
        () => t('validationFieldRequired', { name: t('activity') }),
        (v) => v !== null
      ),
    startDate: yup
      .mixed<Dayjs>()
      .when(['schedulingLabel', 'hasProject'], {
        is: (schedulingLabel: string, hasProject?: boolean) => {
          return schedulingLabel === 'PROJECT' || hasProject;
        },
        then: yup.mixed<Dayjs>().test(
          'project-startDate-in-range',
          () => t('invalidProjectDateRange'),
          (v, ctx) => ctx.parent.startDateIsValid
        ),
        otherwise: yup.mixed<Dayjs>().when(['schedulingLabel'], {
          is: (schedulingLabel: string) => {
            return schedulingLabel === 'WORKPLACE';
          },
          then: yup.mixed<Dayjs>().test(
            'workplace-startDate-in-range',
            () => t('invalidWorkplaceDateRange'),
            (v, ctx) => ctx.parent.startDateIsValid
          ),
          otherwise: yup.mixed<Dayjs>().test(
            'dayjs-valid',
            () => t('invalidDate'),
            (v) => (v ? v.isValid() : false)
          )
        })
      })
      .required(t('validationFieldRequired', { name: t('startDate') })),
    startTime: yup
      .mixed<Dayjs>()
      .test(
        'dayjs-valid',
        () => t('invalidTime'),
        (v) => (v ? v.isValid() : false)
      )
      .required(),
    endTime: yup.mixed<Dayjs>(),
    hourDuration: yup
      .number()
      .default(0)
      .transform(stringToFloat)
      .typeError(t('enterAValidNumericValue'))
      .min(0, t('validationMinNumber', { name: t('duration'), number: 0 }))
      .max(23, t('validationMaxNumber', { name: t('duration'), number: 23 }))
      .required(t('validationFieldRequired', { name: t('duration') })),
    minuteDuration: yup
      .number()
      .default(0)
      .transform(stringToFloat)
      .typeError(t('enterAValidNumericValue'))
      .min(0, t('validationMinNumber', { name: t('duration'), number: 0 }))
      .max(59, t('validationMaxNumber', { name: t('duration'), number: 59 }))
      .required(t('validationFieldRequired', { name: t('duration') }))
      // Note: 'hasProject' condition is temporary work-around to mitigate the backend always returning 'WORKPLACE' as schedulingLabel
      .when(['hourDuration'], {
        is: (hourDuration?: number) => {
          return hourDuration === 0;
        },
        then: yup
          .number()
          .min(1, t('activityPlanning.minimumTotalDuration', { minutes: 1 }))
      }),
    periodType: yup.mixed<'DAY' | 'WEEK'>().when('isRecurring', {
      is: true,
      then: yup
        .mixed<'DAY' | 'WEEK'>()
        .default('WEEK')
        .required(t('validationFieldRequired', { name: t('periodType') }))
    }),
    interval: yup.number().when(['isEditMode', 'isRecurring'], {
      is: (isEditMode: boolean, isRecurring: boolean) =>
        !isEditMode && isRecurring,
      then: yup
        .number()
        .min(1, t('validationMinNumber', { name: t('repeatEvery'), number: 1 }))
        .transform(stringToFloat)
        .typeError(t('enterAValidNumericValue'))
        .nullable()
    }),
    dayInWeekSet: yup
      .array()
      .of(yup.string().uppercase())
      .when(['isEditMode', 'isRecurring', 'periodType'], {
        is: (
          isEditMode: boolean,
          isRecurring: boolean,
          periodType: 'DAY' | 'WEEK'
        ) => !isEditMode && isRecurring && periodType === 'WEEK',
        then: yup
          .array()
          .of(yup.string().uppercase())
          .min(
            1,
            t('validationSelectAtLeast', { number: 1, name: t('option') })
          )
      })
      .default([]),
    locationType: yup.mixed<LocationType>().when('client', {
      is: (client: ClientResponse) => client,
      then: yup
        .mixed<LocationType>()
        .required(t('validationFieldRequired', { name: t('locationType') }))
    }),
    editAppointmentType: yup
      .mixed<'ONLY_THIS' | 'THIS_AND_ALL_SUBSEQUENT'>()
      .when(['isEditMode', 'isRecurring'], {
        is: (isRecurring: boolean, isEditMode: boolean) =>
          isRecurring && isEditMode,
        then: yup
          .mixed<'ONLY_THIS' | 'THIS_AND_ALL_SUBSEQUENT'>()
          .required(
            t('validationFieldRequired', { name: t('editAppointmentType') })
          )
      }),
    description: yup.string().notRequired(),
    schedulingLabel: yup.mixed<SchedulingLabel>().required(),
    // Booleans for conditional logic
    isRecurring: yup.boolean().notRequired(),
    isEditMode: yup.boolean().notRequired(),
    hasProject: yup.boolean().notRequired(),
    startDateIsValid: yup.boolean().notRequired()
  });
};

export default appointmentSchema;
